import { Canvas } from '@react-three/fiber';
import { useState, useRef, useEffect } from 'react';
import { FaTwitter, FaTelegram, FaFire } from 'react-icons/fa';
import Model from './components/Model';
import { ChatInput } from './components/ChatInput';
import { Roadmap } from './components/Roadmap';
import { MusicPlayer } from './components/MusicPlayer';
import Controls from './functions/controls';
import { useAudioAnalysis } from './hooks/useAudioAnalysis';
import { useMousePosition } from './hooks/useMousePosition';
import { useJawRotations } from './hooks/useJawRotations';
import { useResponsiveFOV } from './hooks/useResponsiveFOV';
import * as THREE from 'three';
import { EffectComposer } from '@react-three/postprocessing';
import { Bloom } from '@react-three/postprocessing';
import { Analytics } from "@vercel/analytics/react"
import { Navbar } from './components/Navbar';

const RATE_LIMIT_WINDOW = 10000; // 15 seconds in milliseconds

function App() {
  const [audioLevel, setAudioLevel] = useState(0);
  const { audioContextRef, analyserRef, sourceRef } = useAudioAnalysis(setAudioLevel);
  const mousePosition = useMousePosition();
  const { jawRotations, handleJawRotationChange, handleSavePosition } = useJawRotations();
  const modelRef = useRef();
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const messageTimeoutRef = useRef(null);
  const fov = useResponsiveFOV();

  // Clear error message after 3 seconds
  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => {
        setStatusMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  const handleMessageResponse = async (response) => {
    // Clear any existing timeout
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
    }

    // Set new timeout for response
    messageTimeoutRef.current = setTimeout(() => {
      setStatusMessage('Failed to send message');
      messageTimeoutRef.current = null;
    }, RATE_LIMIT_WINDOW);

    console.log('Handling chat response:', response);
    if (modelRef.current && response.audio) {
      try {
        // Clear timeout on successful response
        clearTimeout(messageTimeoutRef.current);
        messageTimeoutRef.current = null;

        // Log the audio data
        console.log('Audio data length:', response.audio.length);
        console.log('First 100 chars of audio data:', response.audio.substring(0, 100));

        // Create audio blob from base64
        const binaryString = window.atob(response.audio);
        console.log('Binary string length:', binaryString.length);
        
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        console.log('Bytes array length:', bytes.length);

        const audioBlob = new Blob([bytes], { type: 'audio/mpeg' });
        console.log('Created audio blob:', audioBlob);
        
        const audioUrl = URL.createObjectURL(audioBlob);
        console.log('Created audio URL:', audioUrl);
        
        console.log('Playing audio response');
        if (modelRef.current.playAudio) {
          await modelRef.current.playAudio(audioUrl);
        } else {
          console.error('playAudio method not found on model ref');
        }
      } catch (error) {
        console.error('Error processing audio:', error);
        setStatusMessage('Failed to play audio response');
      }
    } else {
      console.log('Missing required data:', {
        hasModel: !!modelRef.current,
        hasAudio: !!response.audio
      });
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="app-container relative h-full" style={{ backgroundImage: 'url(/bg.jpg)', backgroundSize: 'cover', backgroundPosition: 'bottom center' }}>
      <div className="absolute inset-0 bg-black/25 backdrop-blur-sm z-0"></div>

      {/* Status Message */}
      {statusMessage && (
        <div className="absolute top-24 left-1/2 transform -translate-x-1/2 z-50 w-full px-4">
          <div className="px-4 py-2 bg-red-500/80 text-white rounded-lg backdrop-blur-sm animate-fade-out max-w-md mx-auto">
            {statusMessage}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="absolute top-0 w-full text-center z-10 px-4 py-6">
        <h1 className="text-12xl font-pricedown mb-4">SOLSKUNK</h1>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-black/20 backdrop-blur-lg border border-white/10 max-w-full overflow-x-auto">
          <span className="text-sm text-white/50 whitespace-nowrap">CA:</span>
          <code className="px-3 py-1.5 text-sm truncate bg-white/5 rounded-lg font-mono border text-white/75 font-bold border-white/5 overflow-x-auto whitespace-nowrap">
          YogxeCx2moATA6XvZyLjS6Vf6YscygzTvFoCDMipump
          </code>
          <button 
            className="p-2 hover:bg-white/10 rounded-lg transition-colors group flex-shrink-0" 
            onClick={() => navigator.clipboard.writeText('YogxeCx2moATA6XvZyLjS6Vf6YscygzTvFoCDMipump')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white/50 group-hover:text-white transition-colors">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </button>
        </div>
      </div>
      
      {/* 3D Canvas */}
      <div className="absolute inset-0">
        <Canvas camera={{ position: [0, 0, 7.5], fov: fov }}>
          <EffectComposer>
            <Bloom 
              intensity={1.0}
              luminanceThreshold={0.25}
              luminanceSmoothing={0.25}
              height={300}
            />
          </EffectComposer>
          <mesh position={[0, 0, -10]}>

          </mesh>
          <ambientLight intensity={0.8} />
          <spotLight position={[10, 10, 10]} angle={0.0} penumbra={1} />
          <pointLight position={[0, 5, 0]} intensity={2.5} color="#ffffff" />
          <pointLight position={[0, 0, -5]} intensity={1.5} color="#ffffff" />
          <pointLight position={[0, 0, 6.5]} intensity={1.5} color="#ffffff" />
          <Model 
            ref={modelRef}
            mousePosition={mousePosition} 
            audioLevel={audioLevel}
            jawRotations={jawRotations} 
            audioContextRef={audioContextRef}
            analyserRef={analyserRef}
            onAudioStart={() => setIsSpeaking(true)}
            onAudioEnd={() => setIsSpeaking(false)}
          />
        </Canvas>
      </div>

      {/* Chat Input */}
      <div className="absolute bottom-6 left-0 right-0 z-30 px-4 pb-24 pointer-events-none">
        <div className="flex flex-col items-center gap-6 w-fit sm:w-fit max-w-2xl mx-auto pointer-events-auto">
          <ChatInput onMessageSent={handleMessageResponse} />
        </div>
      </div>

      {/* Navbar */}
      <Navbar />

      {/* Music Player 
      <MusicPlayer />*/}
      
      <Analytics/>
    </div>
  );
}

export default App; 